/**
 * Thin, typed wrapper around the official Telegram Mini Apps bridge
 * (window.Telegram.WebApp, loaded via telegram-web-app.js in index.html).
 *
 * We deliberately use the stable global instead of a fast-moving npm SDK so the
 * build never breaks on upstream churn. Only the subset we use is typed here.
 */

export interface TelegramThemeParams {
  bg_color?: string
  text_color?: string
  hint_color?: string
  link_color?: string
  button_color?: string
  button_text_color?: string
  secondary_bg_color?: string
  header_bg_color?: string
  section_bg_color?: string
  destructive_text_color?: string
}

interface TelegramBackButton {
  show: () => void
  hide: () => void
  onClick: (cb: () => void) => void
  offClick: (cb: () => void) => void
}

interface TelegramHaptic {
  impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void
  selectionChanged: () => void
}

export interface TelegramWebApp {
  initData: string
  initDataUnsafe: { user?: { id: number; first_name: string; last_name?: string } }
  colorScheme: 'light' | 'dark'
  themeParams: TelegramThemeParams
  isExpanded: boolean
  ready: () => void
  expand: () => void
  close: () => void
  onEvent: (event: string, cb: () => void) => void
  offEvent: (event: string, cb: () => void) => void
  BackButton: TelegramBackButton
  HapticFeedback: TelegramHaptic
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null
}

/** True when running inside a real Telegram client (non-empty initData). */
export function isTelegramEnvironment(): boolean {
  const wa = getWebApp()
  return !!wa && wa.initData.length > 0
}

/** Raw initData query string to be HMAC-verified by the backend. */
export function getInitData(): string {
  return getWebApp()?.initData ?? ''
}

const THEME_VAR_MAP: Record<keyof TelegramThemeParams, string> = {
  bg_color: '--tg-bg-color',
  text_color: '--tg-text-color',
  hint_color: '--tg-hint-color',
  link_color: '--tg-link-color',
  button_color: '--tg-button-color',
  button_text_color: '--tg-button-text-color',
  secondary_bg_color: '--tg-secondary-bg-color',
  header_bg_color: '--tg-header-bg-color',
  section_bg_color: '--tg-section-bg-color',
  destructive_text_color: '--tg-destructive-text-color',
}

/** Project Telegram theme params onto CSS variables consumed by Tailwind. */
export function applyTelegramTheme(): void {
  const wa = getWebApp()
  if (!wa) return
  const root = document.documentElement
  root.dataset.theme = wa.colorScheme
  for (const [key, cssVar] of Object.entries(THEME_VAR_MAP)) {
    const value = wa.themeParams[key as keyof TelegramThemeParams]
    if (value) root.style.setProperty(cssVar, value)
  }
}

/** Initialise the Mini App: expand, signal ready, sync theme on changes. */
export function initTelegram(): void {
  const wa = getWebApp()
  if (!wa) return
  applyTelegramTheme()
  wa.onEvent('themeChanged', applyTelegramTheme)
  wa.expand()
  wa.ready()
}

export function haptic(type: 'success' | 'error' | 'warning'): void {
  getWebApp()?.HapticFeedback.notificationOccurred(type)
}
